import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, CheckCircle2, Share2, CalendarPlus, Award, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { saveTicketLocal, shareImageDataUrl, isNativeApp, composeTicketPng, openExternal } from "../lib/native";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { Button } from "../components/ui/button";

const WALLET_ENABLED = false; // flip to true once real Apple/Google Wallet integration is live

export default function TicketPage() {
  const { code } = useParams();
  const location = useLocation();
  const { t, lang } = useI18n();
  const { user } = useAuth();

  const initialSub = location.state?.submission;
  const initialForm = location.state?.form;
  const [submission, setSubmission] = useState(initialSub || null);
  const [form, setForm] = useState(initialForm || null);
  const [status, setStatus] = useState(null); // null = loading, {approved: bool}
  const [error, setError] = useState("");

  useEffect(() => {
    if (!submission) {
      setSubmission({ ticket_code: code });
    }
  }, [code, submission]);

  useEffect(() => {
    // Always verify approval status from the server (state can be stale/absent)
    api
      .get(`/ticket/${code}/status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ approved: true }));
  }, [code]);

  const isPending = status ? !status.approved : submission?.approved === false;

  useEffect(() => {
    const tc = submission?.ticket_code || code;
    if (!tc || !status || user === null) return;
    let name = "";
    if (submission?.data) {
      const nk = Object.keys(submission.data).find((k) => /name|اسم/i.test(k));
      if (nk) name = submission.data[nk];
    }
    const doSave = () =>
      saveTicketLocal({
        code: tc,
        title: form ? (lang === "ar" ? form.title_ar || form.title : form.title) : "",
        name,
      });
    if (user === false) {
      // guest flow: keep the ticket on this device
      doSave();
      return;
    }
    // logged in: save locally only if this ticket really belongs to the account
    api
      .get("/my/tickets")
      .then((r) => {
        if ((r.data || []).some((x) => x.ticket_code === tc)) doSave();
      })
      .catch(() => {});
  }, [submission, form, code, lang, status, user]);

  const cardRef = useRef(null);

  const buildTicketImage = () => {
    const tc = submission?.ticket_code || code;
    const qrCanvas = cardRef.current?.querySelector("canvas");
    return composeTicketPng({
      code: tc,
      name: displayName || "",
      eventTitle: title || "",
      eventDate: status?.event_date || "",
      qrCanvas,
      brand: "منصة تسجيل الفعاليات",
      ticketLabel: t("yourTicket"),
      codeLabel: "TICKET CODE",
      nameLabel: t("printedName"),
      dateLabel: t("ticketDateLabel"),
    });
  };

  const handleShare = async () => {
    const tc = submission?.ticket_code || code;
    try {
      const dataUrl = buildTicketImage();
      await shareImageDataUrl({ dataUrl, fileName: `ticket-${tc}.png` });
    } catch (e) {
      toast.error(`${e?.message || e}`);
    }
  };

  const handlePrint = () => window.print();

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-lg mx-auto p-8 text-center text-red-600">{error}</div>
      </div>
    );
  }

  const title = form
    ? lang === "ar" ? form.title_ar || form.title : form.title
    : status
    ? (lang === "ar" ? status.title_ar || status.title : status.title)
    : null;

  // pick a name-ish value from submission data
  let displayName = "";
  if (submission?.data) {
    const nameKey = Object.keys(submission.data).find((k) =>
      /name|اسم/i.test(k)
    );
    if (nameKey) displayName = submission.data[nameKey];
  }

  if (!status && submission?.approved === undefined) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-lg mx-auto p-10 text-center text-slate-400">…</div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
          <div className="print:hidden mb-6">
            <Link to="/" data-testid="ticket-back-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-blue-700">
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
              {t("backHome")}
            </Link>
          </div>
          <div data-testid="pending-card" className="bg-white rounded-3xl border border-amber-200 p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 border border-amber-200 grid place-items-center text-3xl mb-5">
              ⏳
            </div>
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold mb-4">
              {t("pendingApproval")}
            </div>
            {title && (
              <h1 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mb-3">{title}</h1>
            )}
            {displayName && (
              <p className="text-slate-600 mb-3">{t("printedName")}: <span className="font-medium text-slate-900">{displayName}</span></p>
            )}
            <p data-testid="pending-banner" className="text-sm text-slate-600 leading-relaxed">{t("pendingTicketNote")}</p>
            <Link to="/">
              <Button data-testid="ticket-home-btn" className="mt-8 rounded-full bg-blue-600 hover:bg-blue-700">
                {t("backHome")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = !!status?.expired;

  return (
    <div className="min-h-screen bg-[#FDFBF7] print:bg-white">
      <div className="print:hidden">
        <Header />
      </div>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
        <div className="print:hidden mb-6">
          <Link
            to="/"
            data-testid="ticket-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
            {t("backHome")}
          </Link>
        </div>

        <div
          ref={cardRef}
          data-testid="ticket-card"
          className={`relative ${isExpired ? "grayscale opacity-70" : ""}`}
        >
          {isExpired && (
            <div data-testid="ticket-expired-overlay" className="absolute inset-0 z-20 grid place-items-center pointer-events-none">
              <div className="w-full -rotate-12 bg-slate-900/80 text-white text-center py-3 shadow-xl">
                <span dir="ltr" className="font-black text-3xl sm:text-4xl tracking-[0.35em] uppercase">Expired</span>
              </div>
            </div>
          )}
          {/* MAIN PASS */}
          <div className="relative bg-white rounded-t-3xl border border-slate-200 border-b-0 shadow-[0_18px_45px_-18px_rgba(37,99,235,0.35)] px-6 sm:px-8 pt-7 pb-6 overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-amber-500" />

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-amber-500 grid place-items-center text-white font-bold text-lg">
                  V
                </div>
                <div className="text-start">
                  <p className="text-[11px] font-bold text-blue-700 leading-tight">منصة تسجيل الفعاليات</p>
                  <p className="text-[9px] text-slate-400 tracking-[0.25em] uppercase leading-tight mt-0.5">E-Ticket</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t("yourTicket")}
              </div>
            </div>

            {title && (
              <h1 className="mt-5 font-display text-xl sm:text-2xl font-bold text-slate-900 text-start">
                {title}
              </h1>
            )}

            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
              {displayName && (
                <div className="text-start">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-slate-400">{t("printedName")}</p>
                  <p data-testid="ticket-holder-name" className="mt-0.5 text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                </div>
              )}
              {status?.event_date && (
                <div className="text-start">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-slate-400">{t("ticketDateLabel")}</p>
                  <p data-testid="ticket-event-date" className="mt-0.5 text-sm font-semibold text-slate-900">{status.event_date}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col items-center">
              <div className="p-4 rounded-2xl bg-white border-2 border-slate-200">
                <QRCodeSVG
                  value={submission?.ticket_code || code}
                  size={190}
                  level="M"
                  fgColor="#0f172a"
                  bgColor="#ffffff"
                />
              </div>
              <p className="mt-3 text-xs text-slate-500 leading-relaxed text-center max-w-xs">{t("ticketHelp")}</p>
              {/* hidden canvas: used only to compose share/print image */}
              <div className="hidden" aria-hidden="true">
                <QRCodeCanvas
                  value={submission?.ticket_code || code}
                  size={320}
                  includeMargin={false}
                  level="M"
                  fgColor="#0f172a"
                  bgColor="#ffffff"
                />
              </div>
            </div>
          </div>

          {/* PERFORATED DIVIDER */}
          <div className="relative bg-white border-x border-slate-200 h-9">
            <div className="absolute inset-x-7 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-slate-300" />
            <div className="absolute -start-[18px] top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#FDFBF7] border border-slate-200 print:bg-white" />
            <div className="absolute -end-[18px] top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#FDFBF7] border border-slate-200 print:bg-white" />
          </div>

          {/* STUB */}
          <div className="bg-white rounded-b-3xl border border-slate-200 border-t-0 shadow-[0_22px_45px_-20px_rgba(15,23,42,0.3)] px-6 py-5 text-center">
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-400">{t("ticketCode")}</p>
            <p
              data-testid="ticket-code"
              className="mt-1 font-mono text-2xl font-bold text-slate-900 tracking-[0.12em]"
            >
              {submission?.ticket_code || code}
            </p>
          </div>
        </div>

        {WALLET_ENABLED && (
          <div className="mt-6 grid grid-cols-2 gap-3 print:hidden">
          <button
            data-testid="apple-wallet-btn"
            onClick={() => toast.info(t("comingSoon"))}
            className="flex items-center justify-center gap-2.5 rounded-2xl bg-black hover:bg-slate-800 text-white px-4 py-3 transition-colors"
          >
            <Wallet className="w-5 h-5" />
            <span className="text-start leading-tight">
              <span className="block text-[9px] text-white/60 uppercase tracking-wider">{t("addToWallet")}</span>
              <span className="block text-sm font-bold">Apple Wallet</span>
            </span>
          </button>
          <button
            data-testid="google-wallet-btn"
            onClick={() => toast.info(t("comingSoon"))}
            className="flex items-center justify-center gap-2.5 rounded-2xl bg-white hover:bg-slate-50 border-2 border-slate-900 text-slate-900 px-4 py-3 transition-colors"
          >
            <Wallet className="w-5 h-5" />
            <span className="text-start leading-tight">
              <span className="block text-[9px] text-slate-500 uppercase tracking-wider">{t("addToWallet")}</span>
              <span className="block text-sm font-bold">Google Wallet</span>
            </span>
          </button>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3 justify-center print:hidden">
          {status?.checked_in && status?.has_certificate && (
            <Link to={`/certificate/${submission?.ticket_code || code}`}>
              <Button
                data-testid="ticket-certificate-btn"
                variant="outline"
                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                <Award className="w-4 h-4 me-2" />
                {t("certificate")}
              </Button>
            </Link>
          )}
          {status?.event_date && (
            <a
              href={`${API}/ticket/${submission?.ticket_code || code}/calendar.ics`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (isNativeApp()) {
                  e.preventDefault();
                  openExternal(`${API}/ticket/${submission?.ticket_code || code}/calendar.ics`);
                }
              }}
            >
              <Button
                data-testid="ticket-calendar-btn"
                variant="outline"
                className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              >
                <CalendarPlus className="w-4 h-4 me-2" />
                {t("addToCalendar")}
              </Button>
            </a>
          )}
          <Button
            data-testid="ticket-share-btn"
            variant="outline"
            onClick={handleShare}
            className="rounded-full border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            <Share2 className="w-4 h-4 me-2" />
            {t("shareTicket")}
          </Button>
          {!isNativeApp() && (
            <Button
              data-testid="ticket-print-btn"
              variant="outline"
              onClick={handlePrint}
              className="rounded-full"
            >
              <Printer className="w-4 h-4 me-2" />
              {t("print")}
            </Button>
          )}
          <Link to="/">
            <Button
              data-testid="ticket-home-btn"
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              {t("backHome")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
